<template>
  <div class="modal fade plot-details-modal" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-success text-white">
          <h5 class="modal-title text-white">
            <i class="mdi mdi-store me-2"></i>
            Plot Details - {{ plot.plotNumber }}
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <div class="row">
            <!-- Plot Information -->
            <div class="col-md-6">
              <div class="card border-0 shadow-sm mb-3">
                <div class="card-header bg-light">
                  <h6 class="mb-0 fw-bold">
                    <i class="mdi mdi-map-marker me-2 text-success"></i>Plot Information
                  </h6>
                </div>
                <div class="card-body">
                  <table class="table table-sm table-borderless mb-0">
                    <tr>
                      <td class="text-muted" style="width: 40%;">Plot Number:</td>
                      <td class="fw-bold">{{ plot.plotNumber }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Zone/Market:</td>
                      <td><span class="badge bg-info">{{ plot.zone }}</span></td>
                    </tr>
                    <tr>
                      <td class="text-muted">Sub County:</td>
                      <td>{{ plot.subCountyName }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Ward:</td>
                      <td>{{ plot.wardName || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Plot Size:</td>
                      <td>{{ plot.plotSize || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Address:</td>
                      <td>{{ plot.plotAddress || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Status:</td>
                      <td>
                        <span class="badge" :class="statusClass">{{ plot.status || 'Active' }}</span>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>

            <!-- Owner Information -->
            <div class="col-md-6">
              <div class="card border-0 shadow-sm mb-3">
                <div class="card-header bg-light">
                  <h6 class="mb-0 fw-bold">
                    <i class="mdi mdi-account me-2 text-primary"></i>Owner Information
                  </h6>
                </div>
                <div class="card-body">
                  <table class="table table-sm table-borderless mb-0">
                    <tr>
                      <td class="text-muted" style="width: 40%;">Owner Name:</td>
                      <td class="fw-bold">{{ plot.ownerNames || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">ID Number:</td>
                      <td>{{ plot.ownerID || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Phone:</td>
                      <td>
                        <span v-if="plot.ownerPhoneNo">
                          <i class="bx bx-phone text-success me-1"></i>{{ plot.ownerPhoneNo }}
                        </span>
                        <span v-else>-</span>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">Email:</td>
                      <td>
                        <span v-if="plot.ownerEmail">
                          <i class="bx bx-envelope text-primary me-1"></i>{{ plot.ownerEmail }}
                        </span>
                        <span v-else>-</span>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>

            <!-- Financial Information -->
            <div class="col-12">
              <div class="card border-0 shadow-sm">
                <div class="card-header bg-light">
                  <h6 class="mb-0 fw-bold">
                    <i class="mdi mdi-currency-usd me-2 text-warning"></i>Financial Summary
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row text-center">
                    <div class="col-md-2">
                      <div class="border rounded p-2">
                        <small class="text-muted d-block">Annual Rent</small>
                        <strong class="text-success">KES {{ numberFormat(plot.annualRent) }}</strong>
                      </div>
                    </div>
                    <div class="col-md-2">
                      <div class="border rounded p-2">
                        <small class="text-muted d-block">Arrears B/F</small>
                        <strong class="text-danger">KES {{ numberFormat(plot.arrearsForward) }}</strong>
                      </div>
                    </div>
                    <div class="col-md-2">
                      <div class="border rounded p-2">
                        <small class="text-muted d-block">Total Billed</small>
                        <strong class="text-info">KES {{ numberFormat(plot.totalBilled) }}</strong>
                      </div>
                    </div>
                    <div class="col-md-2">
                      <div class="border rounded p-2">
                        <small class="text-muted d-block">Penalty</small>
                        <strong class="text-warning">KES {{ numberFormat(plot.accumulatedPenalty) }}</strong>
                      </div>
                    </div>
                    <div class="col-md-2">
                      <div class="border rounded p-2">
                        <small class="text-muted d-block">Payments</small>
                        <strong class="text-primary">KES {{ numberFormat(plot.totalPayments) }}</strong>
                      </div>
                    </div>
                    <div class="col-md-2">
                      <div class="border rounded p-2" :class="plot.currentBalance > 0 ? 'border-danger' : 'border-success'">
                        <small class="text-muted d-block">Balance</small>
                        <strong :class="plot.currentBalance > 0 ? 'text-danger' : 'text-success'">
                          KES {{ numberFormat(plot.currentBalance) }}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <!-- Last Payment Info -->
                  <div class="row mt-3">
                    <div class="col-md-6">
                      <small class="text-muted">Last Payment Date:</small>
                      <span class="ms-2">{{ plot.lastPaymentDate ? formatDate(plot.lastPaymentDate) : 'No payments recorded' }}</span>
                    </div>
                    <div class="col-md-6">
                      <small class="text-muted">Last Billing Year:</small>
                      <span class="ms-2">{{ plot.lastBillingYear || 'Not yet billed' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Audit Information -->
            <div class="col-12 mt-3">
              <div class="alert alert-light mb-0">
                <small class="text-muted">
                  <strong>Created By:</strong> {{ plot.createdBy }} ({{ plot.createdByIDNo }}) |
                  <strong>Date Created:</strong> {{ formatDate(plot.dateCreated) }}
                  <span v-if="plot.updatedBy">
                    | <strong>Last Updated By:</strong> {{ plot.updatedBy }} on {{ formatDate(plot.dateUpdated) }}
                  </span>
                </small>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PlotDetailsModal',
  props: {
    plot: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    statusClass() {
      switch (this.plot.status) {
        case 'Active': return 'bg-success';
        case 'Vacant': return 'bg-secondary';
        case 'Suspended': return 'bg-danger';
        case 'Transferred': return 'bg-info';
        default: return 'bg-secondary';
      }
    }
  },
  methods: {
    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num || 0);
    },
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    }
  }
}
</script>